import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { INTEGRATION_OPERATION_TYPES, type Operation } from '@blueprint/domain'
import type { IntegrationToken } from '../../data/index.js'
import { Button } from '../../components/controls.js'
import { Dialog } from '../../components/Dialog.js'
import { TabPanel, Tabs } from '../../components/Tabs.js'
import { sessionRepository, useSessionStore } from '../../state/session-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from '../admin/AdminView.module.css'

/**
 * The signed-in user's own account: the name colleagues see, and the way to their password
 * at Teamcentric Accounts.
 *
 * The email is not editable here. It identifies the account across the whole system and
 * decides which tenant it belongs to, so changing it is a tenant admin's decision.
 */
export function ProfileDialog() {
  const open = useUiStore((state) => state.profileOpen)
  const setOpen = useUiStore((state) => state.setProfileOpen)
  const user = useSessionStore((state) => state.user)
  const setUser = useSessionStore((state) => state.setUser)
  const repository = sessionRepository()

  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  // Passwords are changed at Teamcentric Accounts, and so are the name and photo of someone
  // with a teamcentric login.
  const [accountsUrl, setAccountsUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDisplayName(user?.displayName ?? '')
    setError(null)
    setSaved(false)
  }, [open, user])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    repository.authConfig().then(
      (config) => !cancelled && setAccountsUrl(config.accounts && config.accountsUrl ? config.accountsUrl : null),
      () => {},
    )
    return () => {
      cancelled = true
    }
  }, [open, repository])

  if (!open || !user) return null
  // Platform staff from Accounts have their name there too, and their password on its admin pages.
  const nameFromAccounts = accountsUrl !== null && Boolean(user.accountId || user.accountsAdminId)
  const openAccounts = () => window.location.assign(`${accountsUrl}${user.accountsAdminId ? '/admin/account' : '/account'}`)

  const saveName = () => {
    setBusy(true)
    repository
      .updateProfile(displayName)
      .then((updated) => {
        setUser(updated)
        setBusy(false)
        setSaved(true)
      })
      .catch((caught: unknown) => {
        setBusy(false)
        setError(caught instanceof Error ? caught.message : 'That could not be saved.')
      })
  }

  return (
    <Dialog
      open
      title="My Profile"
      onClose={() => setOpen(false)}
      onSubmit={nameFromAccounts ? () => setOpen(false) : saveName}
      footer={
        <>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {nameFromAccounts ? null : (
            <Button variant="primary" type="submit" disabled={busy}>
              Save
            </Button>
          )}
        </>
      }
    >
      <div className={styles.form}>
        <label className={styles.field}>
          <span className={styles.label}>Email</span>
          <input className={styles.input} value={user.email} readOnly disabled />
          <span className={styles.hint}>Ask your administrator to change this.</span>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Display name</span>
          <input
            className={styles.input}
            data-autofocus
            value={displayName}
            maxLength={120}
            disabled={nameFromAccounts}
            onChange={(event) => {
              setDisplayName(event.target.value)
              setSaved(false)
            }}
          />
          <span className={styles.hint}>
            {nameFromAccounts
              ? 'Change your name and photo at teamcentric Accounts.'
              : 'What colleagues see when you share a board.'}
          </span>
        </label>

        {accountsUrl ? (
          <div className={styles.actions}>
            <Button onClick={openAccounts}>Change password</Button>
          </div>
        ) : null}

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
        {saved ? (
          <p className={styles.hint} role="status">
            Saved.
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}

/** Friendly labels for token write operations, shown under their category heading. */
const OPERATION_LABELS: Record<Operation['type'], string> = {
  'board.rename': 'Rename',
  'board.setContext': 'Edit context',
  'group.create': 'Add',
  'group.rename': 'Rename',
  'group.setNotes': 'Edit notes',
  'group.delete': 'Delete',
  'column.create': 'Add',
  'column.rename': 'Rename',
  'column.setNotes': 'Edit notes',
  'column.setGroup': 'Change group',
  'column.move': 'Reorder',
  'column.setContext': 'Edit context',
  'column.delete': 'Delete',
  'swimlane.create': 'Add',
  'swimlane.rename': 'Rename',
  'swimlane.setNotes': 'Edit notes',
  'swimlane.move': 'Reorder',
  'swimlane.setContext': 'Edit context',
  'swimlane.delete': 'Delete',
  'row.create': 'Add',
  'row.rename': 'Rename',
  'row.setNotes': 'Edit notes',
  'row.move': 'Reorder',
  'row.setContext': 'Edit context',
  'row.delete': 'Delete',
  'card.create': 'Add',
  'card.update': 'Edit',
  'card.move': 'Move',
  'card.delete': 'Delete',
  'finding.create': 'Report',
  // Never offered: a token cannot accept or dismiss findings. Present for the type only.
  'finding.resolve': 'Resolve',
}

const OPERATION_CATEGORIES: { prefix: string; title: string }[] = [
  { prefix: 'board', title: 'Board' },
  { prefix: 'group', title: 'Column groups' },
  { prefix: 'column', title: 'Columns' },
  { prefix: 'swimlane', title: 'Swimlanes' },
  { prefix: 'row', title: 'Rows' },
  { prefix: 'card', title: 'Cards' },
  { prefix: 'finding', title: 'Findings' },
]

/** Manages the signed-in user's integration tokens. Secrets exist only until this dialog closes. */
export function AccountSettingsDialog() {
  const open = useUiStore((state) => state.accountSettingsOpen)
  const setOpen = useUiStore((state) => state.setAccountSettingsOpen)
  const repository = sessionRepository()
  const [tab, setTab] = useState<'tokens' | 'new'>('tokens')
  const [tokens, setTokens] = useState<IntegrationToken[]>([])
  const [name, setName] = useState('')
  const [dashboardList, setDashboardList] = useState(true)
  const [dashboardCreate, setDashboardCreate] = useState(false)
  const [boards, setBoards] = useState<{ id: string; name: string; access: string }[]>([])
  const [boardAccess, setBoardAccess] = useState<'all' | 'selected'>('selected')
  const [boardIds, setBoardIds] = useState<string[]>([])
  const [operations, setOperations] = useState<Operation['type'][]>([])
  const [expiry, setExpiry] = useState('30')
  const [secret, setSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  // The board picker floats over the dialog so opening it never changes the dialog's height.
  const [boardMenu, setBoardMenu] = useState<CSSProperties | null>(null)
  const boardTriggerRef = useRef<HTMLButtonElement>(null)
  const boardMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!boardMenu) return
    const inside = (target: EventTarget | null) => target instanceof Node
      && (boardMenuRef.current?.contains(target) || boardTriggerRef.current?.contains(target))
    const onPointerDown = (event: PointerEvent) => { if (!inside(event.target)) setBoardMenu(null) }
    // Escape closes only the picker; without this the dialog's own Escape would close it too.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setBoardMenu(null)
      boardTriggerRef.current?.focus()
    }
    // The panel is fixed to where the trigger was, so scrolling the dialog would detach it.
    const onScroll = (event: Event) => { if (!inside(event.target)) setBoardMenu(null) }
    const onResize = () => setBoardMenu(null)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [boardMenu])

  const toggleBoardMenu = () => {
    const trigger = boardTriggerRef.current
    if (boardMenu || !trigger) {
      setBoardMenu(null)
      return
    }
    const rect = trigger.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom
    // Open upward when there is not room for the full list below the trigger.
    setBoardMenu(below >= 250 || below >= rect.top
      ? { left: rect.left, width: rect.width, top: rect.bottom + 4, maxHeight: Math.min(240, below - 12) }
      : { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + 4, maxHeight: Math.min(240, rect.top - 12) })
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setTab('tokens')
    setBoardMenu(null)
    setName('')
    setDashboardList(true)
    setDashboardCreate(false)
    setBoardIds([])
    setBoardAccess('selected')
    setOperations([])
    setExpiry('30')
    setSecret(null)
    setCopied(false)
    setError(null)
    setLoading(true)
    repository.listIntegrationTokens().then((listed) => {
      if (!cancelled) setTokens(listed)
    }).catch((caught: unknown) => {
      if (!cancelled) setError(caught instanceof Error ? caught.message : 'Tokens could not be loaded.')
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    void (async () => {
      const all: typeof boards = []
      let offset = 0
      while (true) {
        const page = await repository.listBoards({ limit: 100, offset })
        all.push(...page.boards.map((board) => ({ id: board.id, name: board.name, access: board.access })))
        offset += page.boards.length
        if (offset >= page.total || page.boards.length === 0) break
      }
      if (!cancelled) setBoards(all)
    })().catch((caught: unknown) => {
      if (!cancelled) setError(caught instanceof Error ? caught.message : 'Boards could not be loaded.')
    })
    return () => { cancelled = true }
  }, [open, repository])

  const close = () => {
    if (busy) return
    setSecret(null)
    setOpen(false)
  }

  const create = async () => {
    if (busy || secret || !name.trim()) return
    setBusy(true)
    setError(null)
    try {
      const created = await repository.createIntegrationToken({
        name: name.trim(),
        scopes: [
          'boards:read',
          ...(operations.length ? ['boards:write' as const] : []),
          ...(dashboardCreate ? ['boards:create' as const] : []),
        ],
        permissions: { dashboard: { list: dashboardList, create: dashboardCreate }, boardAccess, boardIds: boardAccess === 'all' ? [] : boardIds, operations },
        ...(expiry ? { expiresInDays: Number(expiry) } : {}),
      })
      setTokens((current) => [created.token, ...current])
      setSecret(created.secret)
      setName('')
      setCopied(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The token could not be created.')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (token: IntegrationToken) => {
    if (revokingId || busy) return
    setRevokingId(token.id)
    setError(null)
    try {
      await repository.revokeIntegrationToken(token.id)
      setTokens((current) => current.map((entry) => entry.id === token.id
        ? { ...entry, revokedAt: new Date().toISOString() }
        : entry))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The token could not be revoked.')
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <Dialog
      open={open}
      title="Account Settings"
      wide
      onClose={close}
      onSubmit={create}
      toolbar={
        <Tabs
          idPrefix="account-tokens"
          label="Integration tokens"
          active={tab}
          onChange={(id) => setTab(id as 'tokens' | 'new')}
          tabs={[
            { id: 'tokens', label: 'Your tokens', count: tokens.length },
            { id: 'new', label: 'New token' },
          ]}
        />
      }
      footer={<Button onClick={close} disabled={busy}>Done</Button>}
    >
      <div className={styles.form}>
        <p className={styles.hint}>Integration tokens use your account's board access. Keep each secret private.</p>
        <TabPanel idPrefix="account-tokens" tabId="new" active={tab}>
        <div className={styles.form}>
        {secret ? (
          <div className={styles.field}>
            <strong>Copy this token now</strong>
            <span className={styles.hint}>The secret is shown only once and cannot be retrieved later.</span>
            <input className={styles.input} aria-label="New integration token" value={secret} readOnly onFocus={(event) => event.currentTarget.select()} />
            <div className={styles.actions}>
              <Button onClick={() => {
                if (!navigator.clipboard) {
                  setError('Copy is unavailable. Select and copy the token manually.')
                  return
                }
                void navigator.clipboard.writeText(secret).then(() => setCopied(true)).catch(() => setError('Copy failed. Select and copy the token manually.'))
              }}>Copy token</Button>
              <Button onClick={() => { setSecret(null); setCopied(false); setTab('tokens') }}>I have saved it</Button>
            </div>
            {copied ? <span className={styles.hint} role="status">Copied.</span> : null}
          </div>
        ) : (
          <>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>New token</h3>
              <div className={styles.columns}>
                <label className={styles.field}>
                  <span className={styles.label}>Token name</span>
                  <input className={styles.input} data-autofocus value={name} maxLength={100} onChange={(event) => setName(event.target.value)} placeholder="e.g. Reporting script" />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Expires</span>
                  <select className={styles.input} value={expiry} onChange={(event) => setExpiry(event.target.value)}>
                    <option value="7">In 7 days</option>
                    <option value="30">In 30 days</option>
                    <option value="90">In 90 days</option>
                    <option value="365">In 365 days</option>
                    <option value="">Never</option>
                  </select>
                </label>
              </div>
            </section>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Board access</h3>
              <div className={styles.columns}>
                <fieldset className={styles.choices}>
                  <legend className={styles.label}>Board dashboard</legend>
                  <label><input type="checkbox" checked={dashboardList} onChange={(event) => setDashboardList(event.target.checked)} /> List selected boards</label>
                  <label><input type="checkbox" checked={dashboardCreate} onChange={(event) => setDashboardCreate(event.target.checked)} /> Create new boards</label>
                </fieldset>
                <fieldset className={styles.choices}>
                  <legend className={styles.label}>Boards this token may read</legend>
                  <label><input type="radio" name="token-board-access" checked={boardAccess === 'all'} onChange={() => { setBoardAccess('all'); setBoardMenu(null) }} /> All boards I can access, including future boards</label>
                  <label><input type="radio" name="token-board-access" checked={boardAccess === 'selected'} onChange={() => setBoardAccess('selected')} /> Only selected boards</label>
                  {boardAccess === 'selected' && boards.length > 0 ? <>
                    <button ref={boardTriggerRef} type="button" className={`${styles.input} ${styles.dropdownTrigger}`} aria-expanded={boardMenu !== null} aria-controls={boardMenu ? 'token-board-menu' : undefined} onClick={toggleBoardMenu}>
                      {boardIds.length === 0 ? 'Choose boards' : `${boardIds.length} of ${boards.length} boards selected`}
                    </button>
                    {boardMenu ? <div ref={boardMenuRef} id="token-board-menu" role="group" aria-label="Boards" className={styles.dropdownPanel} style={boardMenu}>
                      {boards.map((board) => <label key={board.id}>
                        <input type="checkbox" checked={boardIds.includes(board.id)} onChange={(event) => setBoardIds((current) => event.target.checked ? [...current, board.id] : current.filter((id) => id !== board.id))} /> {board.name} <span className={styles.hint}>({board.access})</span>
                      </label>)}
                    </div> : null}
                  </> : null}
                  {boardAccess === 'selected' && boards.length === 0 ? <span className={styles.hint}>No boards available.</span> : null}
                </fieldset>
              </div>
            </section>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Allowed write operations</h3>
              <span className={styles.hint}>Applies to selected and newly created boards.</span>
              <div className={styles.categoryGrid}>
                {OPERATION_CATEGORIES.map((category) => {
                  const types: Operation['type'][] = INTEGRATION_OPERATION_TYPES.filter((type) => type.startsWith(`${category.prefix}.`))
                  const selected = types.filter((type) => operations.includes(type)).length
                  return <fieldset key={category.prefix} className={styles.choices}>
                  <legend className={styles.label}>{category.title}</legend>
                  {/* A single-operation category, such as Board, needs no All. */}
                  {types.length > 1 ? <label>
                    <input
                      type="checkbox"
                      checked={selected === types.length}
                      // Part of the category selected shows as a dash rather than a tick.
                      ref={(element) => { if (element) element.indeterminate = selected > 0 && selected < types.length }}
                      onChange={(event) => setOperations((current) => event.target.checked
                        ? [...current, ...types.filter((type) => !current.includes(type))]
                        : current.filter((entry) => !types.includes(entry)))}
                    /> All
                  </label> : null}
                  {types.map((type) => <label key={type}>
                    <input type="checkbox" checked={operations.includes(type)} onChange={(event) => setOperations((current) => event.target.checked ? [...current, type] : current.filter((entry) => entry !== type))} /> {OPERATION_LABELS[type]}
                  </label>)}
                </fieldset>
                })}
              </div>
              <span className={styles.hint}>Deleting a column, swimlane or row can also remove cards inside it. Existing board access still applies.</span>
            </section>
            <div className={styles.actions}>
              <Button variant="primary" type="submit" disabled={busy || !name.trim()}>Create token</Button>
            </div>
          </>
        )}
        </div>
        </TabPanel>

        <TabPanel idPrefix="account-tokens" tabId="tokens" active={tab}>
        {loading ? <p className={styles.hint} role="status">Loading tokens…</p> : (
          <table className={styles.table}>
            <thead><tr><th scope="col">Name</th><th scope="col">Access</th><th scope="col">Expires</th><th scope="col">Status</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
            <tbody>
              {tokens.map((token) => {
                const expired = token.expiresAt !== null && Date.parse(token.expiresAt) <= Date.now()
                return <tr key={token.id}>
                  <td><strong>{token.name}</strong><br /><span className={styles.hint}>{token.prefix}</span></td>
                  <td>{token.permissions ? `${token.permissions.boardAccess === 'all' ? 'All boards' : `${token.permissions.boardIds.length} boards`}, ${token.permissions.operations.length} operations${token.permissions.dashboard.create ? ', create boards' : ''}` : token.scopes.includes('boards:write') ? 'Legacy card write' : 'Legacy read only'}</td>
                  <td>{token.expiresAt ? new Date(token.expiresAt).toLocaleDateString() : 'Never'}</td>
                  <td>{token.revokedAt ? 'Revoked' : expired ? 'Expired' : 'Active'}</td>
                  <td>{!token.revokedAt && !expired ? <Button variant="danger" disabled={revokingId !== null || busy} onClick={() => void revoke(token)}>Revoke</Button> : null}</td>
                </tr>
              })}
              {tokens.length === 0 ? <tr><td className={styles.empty} colSpan={5}>No tokens yet.</td></tr> : null}
            </tbody>
          </table>
        )}
        </TabPanel>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
      </div>
    </Dialog>
  )
}
